import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, TrendingUp, Clock, Building2, AlertTriangle, Zap, CheckCircle2, X as XIcon } from 'lucide-react';
import { format, formatDistance } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { api } from '../api/client';
import { KanbanBoard } from '../components/demandas/KanbanBoard';
import { ETAPAS, ETAPA_MAP, PRIORIDADES } from '../lib/constants';
import { Badge } from '../components/ui/Badge';
import { useAuth } from '../contexts/AuthContext';

export function DashboardPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [demandas, setDemandas] = useState([]);
  const [resumo, setResumo] = useState({});
  const [metricas, setMetricas] = useState(null);
  const [secretarias, setSecretarias] = useState([]);
  const [filtro, setFiltro] = useState({ q: '', secretaria_id: '' });
  const [incluirCanceladas, setIncluirCanceladas] = useState(
    () => localStorage.getItem('comunica.incluirCanceladas') === 'true'
  );
  // Filtro rápido clicando nos cards do topo: 'ativas' | 'novas' | 'em_producao'
  // | 'urgentes' | 'publicadas' | null. Estado simples no front porque é só
  // refinamento da consulta principal.
  const [quickFilter, setQuickFilter] = useState('ativas');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtro.q) params.set('q', filtro.q);
      if (filtro.secretaria_id) params.set('secretaria_id', filtro.secretaria_id);

      // Aplica filtro rápido ANTES de "incluir_canceladas" (que vira override
      // só quando o usuário liga o checkbox dele).
      if (quickFilter === 'novas') params.set('etapa', 'nova_demanda');
      else if (quickFilter === 'em_producao') params.set('etapas', 'em_captacao,em_edicao');
      else if (quickFilter === 'urgentes') {
        params.set('prioridade', 'urgente');
        params.set('apenas_andamento', 'true');
      }
      else if (quickFilter === 'publicadas') params.set('etapa', 'publicado');

      if (incluirCanceladas) params.set('incluir_canceladas', 'true');
      const [dRes, rRes, mRes, sRes] = await Promise.all([
        api.get(`/demandas?${params.toString()}`),
        api.get('/demandas/resumo'),
        api.get('/demandas/metricas'),
        api.get('/secretarias'),
      ]);
      setDemandas(dRes.demandas);
      setResumo(rRes.contadores);
      setMetricas(mRes);
      setSecretarias(sRes.secretarias);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filtro.secretaria_id, incluirCanceladas, quickFilter]);
  useEffect(() => { localStorage.setItem('comunica.incluirCanceladas', String(incluirCanceladas)); }, [incluirCanceladas]);

  const handleSearch = (e) => { e.preventDefault(); load(); };

  const totalAtivas = ETAPAS.filter((e) => !['publicado', 'cancelada'].includes(e.key))
    .reduce((acc, e) => acc + (resumo[e.key] || 0), 0);

  const urgentes = (metricas?.por_prioridade || []).find((p) => p.prioridade === 'urgente')?.total || 0;
  const topSecretarias = (metricas?.por_secretaria || []).slice(0, 5);
  const maxSec = Math.max(1, ...topSecretarias.map((s) => s.total));

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Olá, {user?.nome?.split(' ')[0]} 👋 Pipeline da comunicação em tempo real.</p>
        </div>
        <button className="btn btn-primary" onClick={() => nav('/nova-demanda')}>
          <Plus size={18} /> Nova Demanda
        </button>
      </div>

      <div className="stat-cards" style={{ marginBottom: 20 }}>
        <StatCard
          active={quickFilter === 'ativas'}
          onClick={() => setQuickFilter('ativas')}
          icon={<TrendingUp size={12} />}
          label="Ativas"
          value={totalAtivas}
          hint="Em andamento"
        />
        <StatCard
          active={quickFilter === 'novas'}
          onClick={() => setQuickFilter('novas')}
          color="#0369a1"
          label="Novas"
          value={resumo.nova_demanda || 0}
          hint="Aguardando triagem"
        />
        <StatCard
          active={quickFilter === 'em_producao'}
          onClick={() => setQuickFilter('em_producao')}
          color="#7c3aed"
          icon={<Zap size={12} />}
          label="Em produção"
          value={(resumo.em_captacao || 0) + (resumo.em_edicao || 0)}
          hint="Captação + edição"
        />
        <StatCard
          active={quickFilter === 'urgentes'}
          onClick={() => setQuickFilter('urgentes')}
          color="#dc2626"
          icon={<AlertTriangle size={12} />}
          label="Urgentes"
          value={urgentes}
          hint="Necessitam atenção"
        />
        <StatCard
          active={quickFilter === 'publicadas'}
          onClick={() => setQuickFilter('publicadas')}
          color="#16a34a"
          icon={<CheckCircle2 size={12} />}
          label="Publicadas"
          value={resumo.publicado || 0}
          hint="Concluídas"
        />
      </div>

      {quickFilter && quickFilter !== 'ativas' && (
        <div className="row" style={{ marginBottom: 10, gap: 8 }}>
          <span className="text-xs muted">Filtro:</span>
          <span className="text-sm" style={{ fontWeight: 600 }}>
            {quickFilter === 'novas' && 'Apenas novas demandas'}
            {quickFilter === 'em_producao' && 'Apenas em produção (captação + edição)'}
            {quickFilter === 'urgentes' && 'Apenas urgentes'}
            {quickFilter === 'publicadas' && 'Apenas publicadas'}
          </span>
          <button className="btn btn-ghost btn-sm" onClick={() => setQuickFilter('ativas')}>
            <XIcon size={12} /> Limpar
          </button>
        </div>
      )}

      {metricas && (topSecretarias.length > 0 || (metricas.tempo_medio_horas_por_etapa || []).length > 0) && (
        <div className="grid-2" style={{ marginBottom: 20, gap: 16 }}>
          {topSecretarias.length > 0 && (
            <div className="card">
              <div className="card-header"><strong><Building2 size={14} style={{ verticalAlign: 'middle' }} /> Top secretarias</strong></div>
              <div className="card-body stack" style={{ gap: 10 }}>
                {topSecretarias.map((s) => (
                  <div key={s.nome}>
                    <div className="row" style={{ justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                      <span>{s.nome}</span>
                      <strong>{s.total}</strong>
                    </div>
                    <div style={{ height: 6, background: 'var(--c-surface-2)', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ width: `${(s.total / maxSec) * 100}%`, height: '100%', background: 'var(--c-primary)' }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(metricas.tempo_medio_horas_por_etapa || []).length > 0 && (
            <div className="card">
              <div className="card-header"><strong><Clock size={14} style={{ verticalAlign: 'middle' }} /> Tempo médio por etapa</strong></div>
              <div className="card-body stack" style={{ gap: 8 }}>
                {metricas.tempo_medio_horas_por_etapa.map((t) => (
                  <div key={t.etapa} className="row" style={{ justifyContent: 'space-between', fontSize: 13 }}>
                    <Badge color={ETAPA_MAP[t.etapa]?.color}>{ETAPA_MAP[t.etapa]?.label || t.etapa}</Badge>
                    <span className="muted">
                      {t.horas < 24 ? `${t.horas}h` : `${Math.round(t.horas / 24)} dia${t.horas > 48 ? 's' : ''}`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <form className="row-wrap" style={{ marginBottom: 16 }} onSubmit={handleSearch}>
        <div style={{ position: 'relative', flex: '1 1 260px', minWidth: 200 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-text-muted)' }} />
          <input
            className="input"
            style={{ paddingLeft: 36 }}
            placeholder="Buscar por título, objetivo ou número..."
            value={filtro.q}
            onChange={(e) => setFiltro((f) => ({ ...f, q: e.target.value }))}
          />
        </div>
        <select
          className="select"
          style={{ maxWidth: 260 }}
          value={filtro.secretaria_id}
          onChange={(e) => setFiltro((f) => ({ ...f, secretaria_id: e.target.value }))}
        >
          <option value="">Todas as secretarias</option>
          {secretarias.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
        </select>
        <label className="row" style={{ gap: 6, padding: '0 4px', fontSize: 13, color: 'var(--c-text-muted)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={incluirCanceladas}
            onChange={(e) => setIncluirCanceladas(e.target.checked)}
          />
          Mostrar canceladas
        </label>
        <button className="btn btn-secondary" type="submit">Filtrar</button>
      </form>

      {loading ? (
        <div className="loader-full"><span className="spinner" /></div>
      ) : (
        <KanbanBoard demandas={demandas} onChange={load} />
      )}
    </div>
  );
}

function StatCard({ active, onClick, icon, label, value, hint, color }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`stat-card${active ? ' is-active' : ''}`}
      style={color ? { borderLeftColor: color } : undefined}
    >
      <div className="stat-label">
        {icon && <span style={{ verticalAlign: 'middle', marginRight: 4 }}>{icon}</span>}
        {label}
      </div>
      <div className="stat-value">{value}</div>
      <div className="text-xs muted">{hint}</div>
    </button>
  );
}
